import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BookOpen, GraduationCap, Users, MessageCircle, Sparkles, Tag } from "lucide-react";
import { AuthCard } from "@/components/site/AuthCard";
import { Ladybug } from "@/components/site/Ladybug";
import { useAuth } from "@/context/AuthContext";

const PERKS = [
  [BookOpen, "The Ambassadors Blog", "teachings, ceremony reflections, and wisdom for the path"],
  [GraduationCap, "Your Classes & Courses", "your lessons, waiting whenever you are ready"],
  [Users, "The Community Feed", "a gathering place for kin walking the same road"],
  [MessageCircle, "Private Messaging", "direct connection to your instructors and fellow Ambassadors"],
  [Sparkles, "Ceremony Invitations", "first access to RSVP for the Light Ceremony and other sacred gatherings"],
  [Tag, "Ambassador Discounts", "savings on clinic services, classes, and products"],
];

export default function Ambassadors() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "signup");
  const next = params.get("next");

  useEffect(() => {
    if (params.get("mode")) setMode(params.get("mode") === "login" ? "login" : "signup");
  }, [params]);

  useEffect(() => {
    if (window.location.hash === "#join") {
      setTimeout(() => document.getElementById("join")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [params]);

  const go = (m) => {
    setMode(m);
    document.getElementById("join")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div data-testid="ambassadors-page" className="relative overflow-hidden">
      <div className="absolute -top-24 -right-32 h-96 w-96 rounded-full bg-[#b6689a]/15 blur-3xl breathe" />
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <span className="eyebrow rise">The Healing Ambassadors</span>
          <h1 data-testid="ambassadors-title" className="font-serif-h font-light text-4xl sm:text-5xl lg:text-6xl text-[#577731] leading-[1.1] mt-4 rise d1">
            Step Into the Circle of Ambassadors <Ladybug size={26} rotate={25} className="align-top" />
          </h1>
          <p className="mt-8 text-lg leading-relaxed text-[#232b1b]/80 rise d2">
            Some medicine is not shared in the open. It is passed hand to hand, heart to heart, among those who carry it forward.
          </p>
          <p className="mt-5 text-lg leading-relaxed text-[#232b1b]/80 rise d3">
            The Healing Ambassadors are the keepers and carriers of this work. As an Ambassador, you step inside:
          </p>
          <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-8">
            {PERKS.map(([Icon, title, desc]) => (
              <li key={title} className="flex gap-4">
                <span className="h-11 w-11 shrink-0 rounded-full bg-[#b1cc90]/40 flex items-center justify-center text-[#577731]"><Icon size={18} strokeWidth={1.5} /></span>
                <span><span className="block font-serif-h text-xl text-[#232b1b]">{title}</span><span className="text-sm text-[#232b1b]/70">{desc}</span></span>
              </li>
            ))}
          </ul>
          <p className="font-serif-h italic text-2xl sm:text-3xl text-[#b6689a] mt-14 leading-snug">
            You were never meant to heal alone, and you were never meant to carry the medicine alone.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button onClick={() => go("signup")} data-testid="become-ambassador-btn" className="btn btn-olive">Apply to Become a Healing Ambassador</button>
            <button onClick={() => go("login")} data-testid="ambassador-login-btn" className="btn btn-ghost">Ambassador Login</button>
          </div>
        </div>
        <div className="lg:col-span-5 lg:pt-10" id="join">
          <div className="relative mb-8">
            <img src="/img/community.jpg" alt="Elders, mothers, and young healers gathered in circle" data-testid="ambassadors-image" className="rounded-3xl aspect-[16/10] object-cover w-full" />
            <Ladybug spot="tr" size={24} rotate={-50} />
          </div>
          {user ? (
            <div className="card-soft p-10 text-center" data-testid="already-member-card">
              <p className="font-serif-h text-3xl text-[#577731]">Welcome back, {user.name.split(" ")[0]}.</p>
              <p className="text-sm text-[#232b1b]/70 mt-3">The circle has been holding your seat.</p>
              <Link to={next || "/members"} data-testid="enter-members-btn" className="btn btn-olive mt-8">Enter the Members Area</Link>
            </div>
          ) : (
            <AuthCard mode={mode} setMode={setMode} next={next} />
          )}
        </div>
      </section>
    </div>
  );
}
